import { Paperclip, X } from "lucide-react";
import { Textarea } from "../ui/input";
import RatingPicker from "./RatingPicker";
import { cn } from "../../utils/tailwindUtils";

// One row of a goal/KPI inside an appraisal. Renders differently per role:
//   employee  → self_rating + self_comment + progress note + attachments editable
//   rm        → rm_rating + rm_comment editable; employee fields read-only
//   hr        → hr_override_rating + hr_override_comment editable; all else read-only
// When `readOnly` is true the whole row becomes inert (used in published/closed views).
// `onAttachmentUpload(goalId, file)` and `onAttachmentRemove(attachmentId)`
// are async callbacks the parent wires to the attachment endpoint. Falls back
// to local-only behavior (in-memory list) when callbacks aren't provided so
// the row stays useful in non-API contexts (storybook / preview).
const KPIGoalRow = ({
    goal,
    role,
    readOnly = false,
    onChange,
    onAttachmentUpload,
    onAttachmentRemove,
}) => {
    const set = (patch) => onChange?.({ ...goal, ...patch });

    const empEditable = role === "employee" && !readOnly;
    const rmEditable = role === "rm" && !readOnly;
    const hrEditable = role === "hr" && !readOnly;

    const handleAttachment = async (e) => {
        const files = Array.from(e.target.files || []);
        if (onAttachmentUpload) {
            for (const f of files) {
                await onAttachmentUpload(goal.id, f);
            }
        } else {
            const newOnes = files.map((f) => ({
                id: "att_" + Math.random().toString(36).slice(2),
                original_name: f.name,
                size: f.size,
                type: f.type,
            }));
            set({ attachments: [...(goal.attachments || []), ...newOnes] });
        }
        e.target.value = "";
    };

    const removeAttachment = async (id) => {
        if (onAttachmentRemove) {
            await onAttachmentRemove(id);
        } else {
            set({ attachments: (goal.attachments || []).filter((a) => a.id !== id) });
        }
    };

    return (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 sm:p-5 space-y-4">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div className="min-w-0">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                        {goal.kpi_name}
                    </h3>
                    {goal.expected_outcome && (
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                            Expected: {goal.expected_outcome}
                        </p>
                    )}
                </div>
                <span className="self-start sm:self-auto inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-sidebar/10 text-sidebar dark:bg-sidebar-primary/20 dark:text-sidebar-primary">
                    Weightage {goal.weightage}%
                </span>
            </div>

            {/* Employee section */}
            <div className={cn(
                "rounded-md border p-3 space-y-3",
                "bg-blue-50/50 dark:bg-blue-900/10",
                "border-blue-200/50 dark:border-blue-900/30"
            )}>
                <p className="text-xs font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300">Employee Self-Evaluation</p>

                <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Progress / Notes</label>
                    <Textarea
                        rows={3}
                        value={goal.employee_progress_note || ""}
                        placeholder={empEditable ? "Describe your progress and achievements..." : "—"}
                        disabled={!empEditable}
                        onChange={(e) => set({ employee_progress_note: e.target.value })}
                    />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Self Rating</label>
                        <RatingPicker
                            value={goal.self_rating}
                            disabled={!empEditable}
                            onChange={(v) => set({ self_rating: v })}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Self Comment</label>
                        <Textarea
                            rows={2}
                            value={goal.self_comment || ""}
                            placeholder={empEditable ? "Brief context..." : "—"}
                            disabled={!empEditable}
                            onChange={(e) => set({ self_comment: e.target.value })}
                        />
                    </div>
                </div>

                {/* Attachments */}
                <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Evidence / Attachments</label>
                    {(goal.attachments || []).length > 0 && (
                        <ul className="space-y-1 mb-2">
                            {goal.attachments.map((a) => (
                                <li key={a.id} className="flex items-center justify-between text-xs bg-white dark:bg-gray-900/40 rounded-md border border-gray-200 dark:border-gray-700 px-2 py-1">
                                    <span className="flex items-center gap-1.5 truncate">
                                        <Paperclip className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                                        {a.file ? (
                                            <a
                                                href={a.file}
                                                target="_blank"
                                                rel="noreferrer noopener"
                                                className="truncate hover:text-sidebar-primary hover:underline"
                                                title={a.original_name || a.name}
                                            >
                                                {a.original_name || a.name}
                                            </a>
                                        ) : (
                                            <span className="truncate">{a.original_name || a.name}</span>
                                        )}
                                    </span>
                                    {empEditable && (
                                        <button
                                            type="button"
                                            onClick={() => removeAttachment(a.id)}
                                            className="ml-2 text-gray-400 hover:text-red-500 flex-shrink-0"
                                        >
                                            <X className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                    {empEditable && (
                        <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-dashed border-gray-300 dark:border-gray-600 text-xs text-gray-600 dark:text-gray-300 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                            <Paperclip className="w-3.5 h-3.5" />
                            Add file
                            <input type="file" multiple className="hidden" onChange={handleAttachment} />
                        </label>
                    )}
                    {!empEditable && !(goal.attachments || []).length && (
                        <p className="text-xs text-muted-foreground">No attachments.</p>
                    )}
                </div>
            </div>

            {/* RM section — visible to everyone after RM acts, editable only by RM */}
            {(role !== "employee" || goal.rm_rating !== null) && (
                <div className={cn(
                    "rounded-md border p-3 space-y-3",
                    "bg-indigo-50/50 dark:bg-indigo-900/10",
                    "border-indigo-200/50 dark:border-indigo-900/30"
                )}>
                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700 dark:text-indigo-300">Reporting Manager Review</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">RM Rating</label>
                            <RatingPicker
                                value={goal.rm_rating}
                                disabled={!rmEditable}
                                onChange={(v) => set({ rm_rating: v })}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">RM Comment</label>
                            <Textarea
                                rows={2}
                                value={goal.rm_comment || ""}
                                placeholder={rmEditable ? "Feedback for the employee..." : "—"}
                                disabled={!rmEditable}
                                onChange={(e) => set({ rm_comment: e.target.value })}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* HR override section */}
            {(role === "hr" || goal.hr_override_rating !== null) && (
                <div className={cn(
                    "rounded-md border p-3 space-y-3",
                    "bg-emerald-50/50 dark:bg-emerald-900/10",
                    "border-emerald-200/50 dark:border-emerald-900/30"
                )}>
                    <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">HR Final Override (optional)</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Override Rating</label>
                            <RatingPicker
                                value={goal.hr_override_rating}
                                disabled={!hrEditable}
                                onChange={(v) => set({ hr_override_rating: v })}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Override Reason</label>
                            <Textarea
                                rows={2}
                                value={goal.hr_override_comment || ""}
                                placeholder={hrEditable ? "Required when overriding the RM rating..." : "—"}
                                disabled={!hrEditable}
                                onChange={(e) => set({ hr_override_comment: e.target.value })}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default KPIGoalRow;
